import { flow, types } from 'mobx-state-tree'
import { getAllProcessingEntityConfig } from '../../network/getAllProcessingEntityConfig'
import { getBankCodeApprovals } from '../../network/getBankCodeApprovals'
import { getCardPendingApprovals } from '../../network/getCardPendingApprovals'
import { getCreditDebitAdjustmntsApprovals } from '../../network/getCreditDebitAdjustmntsApprovals'
import { getFeePackageApprovalsReq } from '../../network/getFeePackageApprovalsReq'
import { getFileRoutingApprovals } from '../../network/getFileRoutingApprovals'
import { getGEFilePendingApprovals } from '../../network/getGEFilePendingApprovals'
import { getCommanApprovalReq, getMasterPendingApprovals } from '../../network/getMasterPendingApprovals'
import { getMerchantPayoutPendingApprovals } from '../../network/getMerchantPayoutPendingApprovals'
import { getMerchantPendingApprovals } from '../../network/getMerchantPendingApprovals'
import { getNetbankingPendingApprovals } from '../../network/getNetbankingPendingApprovals'
import { getPackagePendingApprovals } from '../../network/getPackagePendingApprovals'
import { getPackageToInstrumentPendingApprovals } from '../../network/getPackageToInstrumentPendingApprovals'
import { getPackageToMIdPendingApprovals } from '../../network/getPackageToMIdPendingApprovals'
import { getPayoutCmsApprovalsReq } from '../../network/getPayoutCmsApprovalsReq'
import { getRiskControlsMidApprovals } from '../../network/getRiskControlsMidApprovals'
import { getRiskControlsTxnApprovals } from '../../network/getRiskControlsTxnApprovals'
import { getRoutingPendingApprovals } from '../../network/getRoutingPendingApprovals'
import { riskControlMerchantProcessApproval } from '../../network/riskControlMerchantProcessApproval'
import { updateProcessingEntityConfig } from '../../network/updateProcessingEntityConfig'
import { MessageTypes, getErrorMessage } from '../../utils'
import { RootStore } from '../store'
import { BankCode } from './BankingCode'
import { CardItem } from './Card'
import { CreditAdjustment } from './CreditAdjustment'
import { Entity } from './EntityMaster'
import { ExcelFile } from './GenerateExcelFile'
import { Merchant } from './Merchant'
import { Config as MerchantPayout } from './MerchantPayoutConfig'
import { NetBank } from './Netbanking'
import { Package } from './Package'
import { Instrument } from './PackageToInstrument'
import { PackageMId } from './PackageToMId'
import { PayoutCms } from './PayoutCms'
import { Transaction } from './RiskConfig'
import { Rule } from './Routing'

const FileUpload = types.model('fileUpload', {
  id: types.optional(types.number, 0),
  fileName: types.optional(types.string, ''),
  link: types.optional(types.string, ''),
  createdDate: types.optional(types.string, ''),
  fileType: types.optional(types.string, ''),
})

const Approval = types.model('Approval', {
  id: types.maybeNull(types.number),
  key: types.optional(types.string, ''),
  data: types.union(
    NetBank,
    CardItem,
    Merchant,
    Rule,
    Package,
    Instrument,
    PackageMId,
    ExcelFile,
    Entity,
    FileUpload,
    MerchantPayout,
    Transaction,
  ),
  type: types.optional(types.string, ''),
  className: types.optional(types.string, ''),
  username: types.optional(types.string, ''),
  approvalType: types.optional(types.string, ''),
  approvalStatus: types.optional(types.boolean, false),
  fileUploadData: types.optional(FileUpload, {}),
  bankingCodeData: types.optional(BankCode, {}),
  merchantPayoutData: types.optional(MerchantPayout, {}),
  crDrAdjustmentData: types.optional(CreditAdjustment, {}),
  payoutCmsData: types.optional(PayoutCms, {}),
  // link: types.optional(types.string, '')
})

// TODO:
const Approvals = types
  .model('Approvals', {
    entity: types.array(Approval),
    card: types.array(Approval),
    netBanking: types.array(Approval),
    merchant: types.array(Approval),
    routingRule: types.array(Approval),
    package: types.array(Approval),
    packageInstrument: types.array(Approval),
    packageMId: types.array(Approval),
    excelFile: types.array(Approval),
    fileUpload: types.array(Approval),
    bankingCode: types.array(Approval),
    merchantPayout: types.array(Approval),
    crDrAdjustment: types.array(Approval),
    riskControlTxn: types.array(Approval),
    riskControlMid: types.array(Approval),
    payoutCms: types.array(Approval),
  })
  .actions((self) => ({
    getPendingCardApprovals: flow(function* getPendingCardApprovals(isElevated: boolean) {
      try {
        const response = yield getCardPendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.card = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingMasterApprovals: flow(function* getPendingMasterApprovals(
      isElevated: boolean,
      data: any,
      paginationInfo: any,
      fileType: any,
      type: any,
    ) {
      try {
        const request = getCommanApprovalReq({ ...data, fileType }, paginationInfo)
        const response = yield getMasterPendingApprovals(isElevated, request, type)
        // const own = self
        // own.entity = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingNetbankingApprovals: flow(function* getPendingNetbankingApprovals(isElevated: boolean) {
      try {
        const response = yield getNetbankingPendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.netBanking = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingMerchantApprovals: flow(function* getPendingMerchantApprovals(isElevated: boolean) {
      try {
        const response = yield getMerchantPendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.merchant = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingRoutingApprovals: flow(function* getPendingRoutingApprovals(isElevated: boolean, fileType: any) {
      try {
        const response = yield getRoutingPendingApprovals(isElevated, RootStore.session.getTokens, fileType)
        const own = self
        own.routingRule = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getFileRoutingPendingApprovals: flow(function* getFileRoutingPendingApprovals(isElevated: boolean, fileType: any) {
      try {
        const response = yield getFileRoutingApprovals(isElevated, RootStore.session.getTokens, fileType)
        const own = self
        own.fileUpload = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingPackageApprovals: flow(function* getPendingPackageApprovals(isElevated: boolean, fileType: any) {
      try {
        const response = yield getPackagePendingApprovals(isElevated, RootStore.session.getTokens, fileType)
        const own = self
        own.package = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingPackageToInstrumentApprovals: flow(function* getPendingPackageToInstrumentApprovals(isElevated: boolean) {
      try {
        const response = yield getPackageToInstrumentPendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.packageInstrument = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingPackageToMIdApprovals: flow(function* getPendingPackageToMIdApprovals(
      isElevated: boolean,
      fileType: any,
    ) {
      try {
        const response = yield getPackageToMIdPendingApprovals(isElevated, RootStore.session.getTokens, fileType)
        const own = self
        own.packageMId = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingGenerteFileApprovals: flow(function* getPendingGenerteFileApprovals(isElevated: boolean) {
      try {
        const response = yield getGEFilePendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.excelFile = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPendingBankingCodeApprovals: flow(function* getPendingBankingCodeApprovals(isElevated: boolean) {
      try {
        const response = yield getBankCodeApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.bankingCode = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getMerchantPayoutConfigApprovals: flow(function* getMerchantPayoutConfigApprovals(isElevated: boolean) {
      try {
        const response = yield getMerchantPayoutPendingApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.merchantPayout = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getFeePackageApprovals: flow(function* getFeePackageApprovals(isElevated: boolean) {
      try {
        const response = yield getFeePackageApprovalsReq(isElevated, RootStore.session.getTokens, 1)
        const own = self
        own.merchantPayout = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getCrDrAdjustmentApprovals: flow(function* getCrDrAdjustmentApprovals(isElevated: boolean) {
      try {
        const response = yield getCreditDebitAdjustmntsApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.crDrAdjustment = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getRiskControlTransactionApprovals: flow(function* getRiskControlTransactionApprovals(isElevated: boolean) {
      try {
        const response = yield getRiskControlsTxnApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.riskControlTxn = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getRiskControlMidApprovals: flow(function* getRiskControlMidApprovals(isElevated: boolean) {
      try {
        const response = yield getRiskControlsMidApprovals(isElevated, RootStore.session.getTokens)
        const own = self
        own.riskControlMid = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getPayoutCmsApprovals: flow(function* getPayoutCmsApprovals(isElevated: boolean) {
      try {
        const response = yield getPayoutCmsApprovalsReq(isElevated, RootStore.session.getTokens)
        const own = self
        own.payoutCms = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    processRiskControlMerchantProcessApproval: flow(function* processRiskControlMerchantProcessApproval(data: any) {
      try {
        const response = yield riskControlMerchantProcessApproval(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Approved', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    downloadEntityConfiguration: flow(function* downloadEntityConfiguration(data: any) {
      try {
        const response = yield getAllProcessingEntityConfig(data)
        if (response?.data?.length === 0) {
          throw { message: 'No file found' }
        }

        const blob = new Blob([JSON.stringify(response?.data)], {
          type: 'application/json',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = data?.processingEntityId + '_' + data?.configType + '_entity_configuration.json'
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        RootStore.message.setMessage('file downloaded sucessfully', MessageTypes.SUCCESS)
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateEntityConfiguration: flow(function* updateEntityConfiguration(data: any) {
      try {
        const response = yield updateProcessingEntityConfig(data)
        console.log('resonse=', response)
        RootStore.message.setMessage('Entity updated sucessfully', MessageTypes.SUCCESS)
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getEntityList() {
      const result = self.entity.map((item) => {
        const entity: any = item.data
        return {
          ...item,
          data: {
            id: entity?.id,
            key: item?.key,
            info: {
              name: entity?.name,
              type: entity?.type,
              status: entity?.status === true ? 'Enabled' : entity?.status === false ? 'Disabled' : entity?.status,
              supportNetBanking: entity?.supportNetBanking,
              supportCard: entity?.supportCard,
            },
            endpoint: {
              domain: entity?.endpointInfo?.domain,
              status: entity?.endpointInfo?.status,
              crypto: entity?.endpointInfo?.checksumEncryptionSelect,
              encryptionType: entity?.endpointInfo?.encryptionType,
              redirectUrl: entity?.endpointInfo?.redirectUrl,
              enrollUrl: entity?.endpointInfo?.enrollUrl,
              authUrl: entity?.endpointInfo?.authUrl,
              stqUrl: entity?.endpointInfo?.stqUrl,
              s2sUrl: entity?.endpointInfo?.s2sUrl,
              rpslS2sUrl: entity?.endpointInfo?.rpslS2sUrl,
            },
            contactInfo: entity?.contacts,
            ipDetails: entity?.publicIps?.map((ipAdd: any, index: any) => ({
              id: index,
              publicIp: ipAdd,
            })),
          },
        }
      })
      return result
    },
    getFilteredEntityList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.entity ?? []
      const data = list?.map((item: any) => {
        const entity: any = item.data
        return {
          ...item,
          data: {
            id: entity?.id,
            key: item?.key,
            info: {
              name: entity?.name,
              type: entity?.type,
              status: entity?.status === true ? 'Enabled' : entity?.status === false ? 'Disabled' : entity?.status,
              supportNetBanking: entity?.supportNetBanking,
              supportCard: entity?.supportCard,
            },
            endpoint: {
              domain: entity?.endpointInfo?.domain,
              status: entity?.endpointInfo?.status,
              crypto: entity?.endpointInfo?.checksumEncryptionSelect,
              encryptionType: entity?.endpointInfo?.encryptionType,
              redirectUrl: entity?.endpointInfo?.redirectUrl,
              enrollUrl: entity?.endpointInfo?.enrollUrl,
              authUrl: entity?.endpointInfo?.authUrl,
              stqUrl: entity?.endpointInfo?.stqUrl,
              s2sUrl: entity?.endpointInfo?.s2sUrl,
              rpslS2sUrl: entity?.endpointInfo?.rpslS2sUrl,
            },
            contactInfo: entity?.contacts,
            ipDetails: entity?.publicIps?.map((ipAdd: any, index: any) => ({
              id: index,
              publicIp: ipAdd,
            })),
          },
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredCardList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.card ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredNetBankingList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.netBanking ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredMerchantList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.merchant ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilterdRoutingRuleList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.fileUpload ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
          key: item.key === '' ? 'null' : item?.key,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredBankingCodesList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.bankingCode ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredPackageList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.package ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredPackageInstrument(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.packageInstrument ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredPackageMid(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.packageMId ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredExcelFileList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.excelFile ?? []
      const data = list?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredMerchantPayoutConfigList(listData: any, keys?: any, searchText?: any) {
      const data = listData?.map((item: any) => {
        return {
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key]?.toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val?.includes(obj?.searchString?.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return data.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key]?.toLowerCase()?.includes(searchText?.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredCrDrAdjustmentList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.crDrAdjustment ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredPayoutCmsList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.payoutCms ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredRiskControlTxnList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.riskControlTxn ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getFilteredRiskControlMidList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.riskControlMid ?? []
      const data = list?.map((item: any) => {
        return {
          id: item?.id,
          key: item?.key,
          username: item?.username,
          approvalType: item?.approvalType,
          ...item,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
  }))

const approvalData = Approvals.create({})

export { Approval, Approvals, approvalData }
